// Layer 4 (UI) — Quality & Lab, rebuilt to the locked comp
// `.impeccable/mocks/decision/quality.html` (A · Sample pipeline strip, picked
// 25 Sep, polish.P10 #658). Header on the bare canvas (title; the cadence
// switch and help on the right), ink pill tabs with their headline counts,
// then per tab: one band, one 8/4 row, at most one full-width panel.
// Every figure comes from Scene.transform.ts / ConcentratePenaltyPanel.transform.ts.

import { useEffect, useState, type ReactNode } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import {
  Button,
  GlassCard,
  HelpAnchor,
  SurfacePageBody,
  SurfacePageHeader,
  SurfacePageLayout,
  SurfacePageTabs,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/design-system';
import { cn } from '@/shared/cn';
import { useDrawer } from '@/app/providers';
import { Commentable } from '@/shared/comments/Commentable';
import { useScenario } from '@/versions/v1/shared/ScenarioProvider';
import { useAdvisory } from '@/versions/v1/shared/AdvisoryProvider';
import { StateChip } from '@/versions/v1/shared/StateChip';
import { projectTileBySlug } from '@/versions/v1/shared/project-seed';
import { projectSidebarHref } from '@/versions/v1/shared/project-sidebar-nav';
import type { PlantCadence } from '@/versions/v1/schema/unit-operation';
import { MeterBar, TrendChart } from '@/versions/v1/modules/_viz';
import { ActorChip } from '../advisory/PipelineStrip';
import { CARD_TABLE_DENSE, DowntimeCard, HOT_ROW, RankBar, SYN_CHIP } from '../downtime/DowntimeCard';
import { ConcentratePenaltyPanel } from './ConcentratePenaltyPanel';
import { CaseCodeChip, Comparison, DivergingBar, PanelFigure, StateChipPill, StripCell } from './QualityUi';
import {
  IMPURITIES_ACTION,
  MASS_BALANCE_SCALE_PCT,
  MASS_BALANCE_TOLERANCE_PCT,
  PIPELINE_CELL_STATES,
  QUALITY_TABS,
  REAGENT_REORDER_DAYS,
  REAGENT_SCALE_DAYS,
  arsenicArtefactCheckLine,
  arsenicCircuitCells,
  arsenicTrend30d,
  caseCodeFor,
  closureBarGeometry,
  dueNowCount,
  isQualityTab,
  massBalanceMeters,
  missedCaseIdIn,
  osaCoverageLabel,
  projectRegisterTable,
  qualityCopy,
  qualityTabCounts,
  reagentHeadline,
  reagentSupplyMeters,
  registerRowsView,
  sampleSourceCount,
  sampleSourceInventory,
  samplePipelineRead,
  samplesDueLists,
  samplesDueRowsView,
  setpointHeadline,
  setpointMetersForRegister,
  setpointTargetPct,
  tablesForQualityTab,
  type QualityTab,
  type SampleDueRowView,
  type SamplePipelineCellId,
  type SamplePipelineRead,
} from './Scene.transform';

const HELP_DOC: Record<QualityTab, string> = {
  'sample-register': 'quality/sample-register',
  impurities: 'quality/impurities',
  'samples-due': 'quality/samples-due',
};

// Container width at which the 8/4 rows engage: `@[1000px]:` (written out
// literally below so Tailwind's scanner sees every class).

type OpenCase = (caseId: string, title: string) => void;

export function QualityScene() {
  const { projectId } = useParams<{ projectId: string }>();
  const project = projectTileBySlug(projectId);
  const copy = qualityCopy();
  const { open: openDrawer } = useDrawer();
  const { state, setCadence } = useScenario();
  const advisory = useAdvisory();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const tab: QualityTab = isQualityTab(tabParam) ? tabParam : 'sample-register';
  const cadenceParam = searchParams.get('cadence');
  // C's rank-one mark: the Missed cell is selected when the register opens.
  const [selectedCell, setSelectedCell] = useState<SamplePipelineCellId | null>('missed');

  useEffect(() => {
    document.title = project ? `${copy.title} · ${project.name}` : `${copy.title} · OptiFlow`;
  }, [copy.title, project]);

  // `?cadence=` deep link (paul-call.A8 #256), now page-level.
  useEffect(() => {
    if (cadenceParam === 'osa' && state.cadence !== 'osa') setCadence('osa');
    if (cadenceParam === 'lab' && state.cadence !== 'lab') setCadence('lab');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cadenceParam]);

  const openCaseDrawer: OpenCase = (caseId, title) => {
    openDrawer('case-drawer', { kicker: 'Case', title, wide: true, data: { caseId } });
  };

  const rawRegister = tablesForQualityTab('sample-register').find((table) => table.caption.startsWith('Sample register'));
  const registerTable = rawRegister ? projectRegisterTable(rawRegister, state.cadence) : undefined;
  const lists = samplesDueLists(state, advisory);
  const pipeline = registerTable ? samplePipelineRead(registerTable, lists, state.cursor) : null;
  const counts = pipeline ? qualityTabCounts(pipeline) : null;
  const tabItems = QUALITY_TABS.map((item) => ({ ...item, count: counts?.[item.id] }));

  const selectTab = (next: QualityTab) => {
    const params = new URLSearchParams(searchParams);
    params.set('tab', next);
    setSearchParams(params, { replace: true });
    if (next === 'sample-register') setSelectedCell('missed');
  };

  const selectCadence = (next: PlantCadence) => {
    setCadence(next);
    const params = new URLSearchParams(searchParams);
    params.set('cadence', next);
    setSearchParams(params, { replace: true });
  };

  const selectCell = (cell: SamplePipelineCellId) => {
    if (cell === 'due') {
      selectTab('samples-due');
      return;
    }
    setSelectedCell((current) => (current === cell ? null : cell));
  };

  if (!project) {
    return (
      <SurfacePageLayout width="full">
        <SurfacePageBody>
          <SurfacePageHeader title="Plant not found" />
        </SurfacePageBody>
      </SurfacePageLayout>
    );
  }

  return (
    <SurfacePageLayout width="full">
      <Commentable name="QualityScene">
        <SurfacePageBody className="@container min-h-[calc(100vh-3.2rem)] space-y-3" width="full">
          <SurfacePageHeader
            title={copy.title}
            titleSize="large"
            actions={
              <>
                <Commentable name="CadenceControl">
                  <CadenceSegment cadence={state.cadence} onChange={selectCadence} />
                </Commentable>
                <HelpAnchor doc={HELP_DOC[tab]} label="Quality & Lab" />
              </>
            }
          />
          <SurfacePageTabs aria-label="Quality & Lab" items={tabItems} value={tab} onChange={selectTab} variant="ink" />

          {tab === 'sample-register' && registerTable && pipeline ? (
            <Commentable name="SampleRegister">
              <SampleRegisterTab
                pipeline={pipeline}
                registerTable={registerTable}
                cadence={state.cadence}
                projectSlug={project.slug}
                selectedCell={selectedCell}
                onSelectCell={selectCell}
                onOpenCase={openCaseDrawer}
              />
            </Commentable>
          ) : null}
          {tab === 'impurities' ? (
            <Commentable name="Impurities">
              <ImpuritiesTab onOpenCase={openCaseDrawer} />
            </Commentable>
          ) : null}
          {tab === 'samples-due' ? (
            <Commentable name="SamplesDue">
              <SamplesDueTab lists={lists} onOpenCase={openCaseDrawer} />
            </Commentable>
          ) : null}
        </SurfacePageBody>
      </Commentable>
    </SurfacePageLayout>
  );
}

/** The sample-cadence switch in the page header (comp `.seg.sm`): it projects
 *  the register, the sample points, Samples Due and the TopBar LIMS chip. */
function CadenceSegment({ cadence, onChange }: { cadence: PlantCadence; onChange: (next: PlantCadence) => void }) {
  const options: { id: PlantCadence; label: string }[] = [
    { id: 'lab', label: 'Site lab · hours' },
    { id: 'osa', label: 'OSA · 30 min' },
  ];
  return (
    <div
      role="group"
      aria-label="Sample cadence"
      className="inline-flex items-center gap-0.5 rounded-full border border-[var(--hairline-strong)] bg-[var(--surface-1)] p-[3px]"
    >
      {options.map((option) => (
        <button
          key={option.id}
          type="button"
          aria-pressed={option.id === cadence}
          onClick={() => onChange(option.id)}
          className={cn(
            'whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium transition-colors duration-150',
            option.id === cadence
              ? 'bg-ink-primary text-[var(--surface-2)]'
              : 'text-ink-secondary hover:bg-[var(--surface-2)] hover:text-ink-primary',
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------- register

function SamplePipelineStrip({
  pipeline,
  selectedCell,
  onSelectCell,
  onOpenCase,
}: {
  pipeline: SamplePipelineRead;
  selectedCell: SamplePipelineCellId | null;
  onSelectCell: (cell: SamplePipelineCellId) => void;
  onOpenCase: OpenCase;
}) {
  return (
    <GlassCard
      as="nav"
      tone="matte"
      aria-label="Sample pipeline"
      data-sample-pipeline
      className="overflow-hidden rounded-2xl border-[var(--hairline-strong)] p-0"
    >
      <div className="grid grid-cols-2 @[760px]:grid-cols-4 [&>*]:min-h-[7.25rem]">
        {pipeline.cells.map((cell) => (
          <StripCell
            key={cell.id}
            id={cell.id}
            name={cell.name}
            figure={cell.figure}
            figureContext={cell.figureContext}
            selected={selectedCell === cell.id}
            onSelect={() => onSelectCell(cell.id)}
            className="border-b border-[var(--hairline)] @[760px]:border-b-0 after:hidden @[760px]:after:block"
            foot={
              cell.id === 'missed' ? (
                <>
                  <StateChip state="MISS" />
                  {cell.caseLink ? (
                    <>
                      <span className="text-xs text-muted">discounts</span>
                      <CaseCodeChip
                        code={cell.caseLink.code}
                        label={cell.lines[0]?.lead ?? cell.name}
                        onOpen={() => onOpenCase(cell.caseLink!.caseId, `${cell.lines[0]?.lead ?? 'Sample'} missed`)}
                      />
                    </>
                  ) : null}
                </>
              ) : cell.actor ? (
                <ActorChip actor={cell.actor.kind}>{cell.actor.label}</ActorChip>
              ) : null
            }
          >
            {cell.lines.map((line, index) => (
              <Comparison key={index} pre={line.pre} lead={line.lead} rest={line.rest} />
            ))}
          </StripCell>
        ))}
      </div>
    </GlassCard>
  );
}

function SampleRegisterTab({
  pipeline,
  registerTable,
  cadence,
  projectSlug,
  selectedCell,
  onSelectCell,
  onOpenCase,
}: {
  pipeline: SamplePipelineRead;
  registerTable: ReturnType<typeof projectRegisterTable>;
  cadence: PlantCadence;
  projectSlug: string;
  selectedCell: SamplePipelineCellId | null;
  onSelectCell: (cell: SamplePipelineCellId) => void;
  onOpenCase: OpenCase;
}) {
  const rows = registerRowsView(registerTable);
  const hotStates = selectedCell ? PIPELINE_CELL_STATES[selectedCell] : [];
  const setpoint = setpointHeadline();
  const setpointTarget = setpointTargetPct();
  const reagentTable = tablesForQualityTab('sample-register').find((table) => table.caption.startsWith('Reagent inventory'));
  const reagents = reagentTable ? reagentSupplyMeters(reagentTable) : [];
  const reagent = reagentHeadline(reagents);

  return (
    <div className="space-y-3">
      <SamplePipelineStrip pipeline={pipeline} selectedCell={selectedCell} onSelectCell={onSelectCell} onOpenCase={onOpenCase} />

      <div className={`grid min-w-0 gap-3 @[1000px]:grid-cols-12 @[1000px]:items-start`}>
        <DowntimeCard
          title="Sample register · today vs LIMS"
          meta={osaCoverageLabel(cadence)}
          className={`@[1000px]:col-span-8`}
          aria-label="Sample register"
        >
          <div className={CARD_TABLE_DENSE} data-register-table>
            <Table wrap={false} className="tabular-nums">
              <THead>
                <TR>
                  <TH>Stream</TH>
                  <TH>Freq</TH>
                  <TH>Collected</TH>
                  <TH>LIMS</TH>
                  <TH>Status</TH>
                  <TH className="text-right">TAT</TH>
                  <TH>Tier</TH>
                  <TH>Method</TH>
                </TR>
              </THead>
              <TBody>
                {rows.map((row) => {
                  const hot = row.state !== null && hotStates.includes(row.state);
                  const chip = row.state ? (
                    <StateChip state={row.state} />
                  ) : (
                    row.statusText
                  );
                  return (
                    <TR
                      key={row.id}
                      data-register-row={row.id}
                      data-hot={hot ? 'true' : undefined}
                      className={hot ? cn(HOT_ROW, '[&>td:first-child]:shadow-[inset_3px_0_0_var(--color-accent-500)]') : undefined}
                    >
                      <TD className="whitespace-nowrap font-semibold !text-ink-primary">
                        {row.stream}
                        {row.samplePointId ? (
                          <span className="ml-1.5 font-mono text-[10px] text-muted">{row.samplePointId}</span>
                        ) : null}
                      </TD>
                      <TD className="whitespace-nowrap">{row.freq}</TD>
                      <TD className="whitespace-nowrap">{row.collected}</TD>
                      <TD className="whitespace-nowrap">{row.lims}</TD>
                      <TD className="whitespace-nowrap">
                        {row.caseId ? (
                          <button
                            type="button"
                            aria-label={`Open case ${caseCodeFor(row.caseId)} · ${row.stream}`}
                            onClick={() => onOpenCase(row.caseId!, `${row.stream} ${row.slot} missed`)}
                            className="inline-flex rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-500)]"
                          >
                            {chip}
                          </button>
                        ) : (
                          chip
                        )}
                      </TD>
                      <TD className="whitespace-nowrap text-right">{row.tat}</TD>
                      <TD className="whitespace-nowrap">{row.tier}</TD>
                      <TD className="whitespace-nowrap !text-muted">{row.method}</TD>
                    </TR>
                  );
                })}
              </TBody>
            </Table>
          </div>
        </DowntimeCard>

        <div className={`grid min-w-0 gap-3 @[640px]:grid-cols-2 @[1000px]:col-span-4 @[1000px]:grid-cols-1`}>
          <DowntimeCard title="Process setpoint compliance" meta={`% of shift in range · tick = ${setpointTarget}%`}>
            <PanelFigure id="setpoint" figure={setpoint.figure} lead={setpoint.lead} rest={setpoint.rest} />
            <div className="space-y-2" data-setpoint-rows>
              {setpointMetersForRegister().map((meter) => (
                <MetricRow key={meter.id} label={meter.label} value={meter.valueLabel ?? `${meter.value}%`}>
                  <MeterBar
                    value={meter.value}
                    max={meter.max ?? 100}
                    marker={meter.marker}
                    markerInTrack
                    markerCaption={false}
                    tone={meter.tone}
                  />
                </MetricRow>
              ))}
            </div>
          </DowntimeCard>
          <DowntimeCard title="Reagent supply" meta={`days · tick = ${REAGENT_REORDER_DAYS}-day reorder`}>
            <PanelFigure id="reagent" figure={reagent.figure} figureContext={reagent.figureContext} lead={reagent.lead} rest={reagent.rest} />
            <div className="space-y-2" data-reagent-rows>
              {reagents.map((meter) => (
                <MetricRow
                  key={meter.id}
                  label={meter.name}
                  sub={meter.role}
                  value={meter.valueLabel}
                >
                  <MeterBar
                    value={meter.days}
                    max={REAGENT_SCALE_DAYS}
                    marker={{ value: REAGENT_REORDER_DAYS, label: `${REAGENT_REORDER_DAYS}-day reorder` }}
                    markerInTrack
                    markerCaption={false}
                    tone={meter.tone}
                  />
                </MetricRow>
              ))}
            </div>
          </DowntimeCard>
        </div>
      </div>

      <MassBalanceCard onOpenCase={onOpenCase} projectSlug={projectSlug} />
      <SamplePointsDisclosure cadence={cadence} />
    </div>
  );
}

/** Label | bar | value on one line (comp `.brow`). */
function MetricRow({ label, sub, value, children }: { label: string; sub?: string; value: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[minmax(0,9.5rem)_minmax(0,1fr)_2.75rem] items-center gap-2.5 text-xs" data-metric-row={label}>
      <span className="min-w-0 truncate font-medium text-ink-primary" title={sub ? `${label} · ${sub}` : label}>
        {label}
        {sub ? <span className="ml-1.5 font-normal text-muted">{sub}</span> : null}
      </span>
      {children}
      <span className="whitespace-nowrap text-right font-semibold tabular-nums text-ink-primary">{value}</span>
    </div>
  );
}

function MassBalanceCard({ onOpenCase, projectSlug }: { onOpenCase: OpenCase; projectSlug: string }) {
  const rows = massBalanceMeters();
  return (
    <DowntimeCard
      title="Mass balance closure · today"
      meta={`bar = error · shaded = ±${MASS_BALANCE_TOLERANCE_PCT}% tolerance`}
      aria-label="Mass balance closure"
    >
      <div className={`grid gap-4 @[1000px]:grid-cols-[repeat(3,minmax(0,1fr))_auto] @[1000px]:gap-0`} data-closure>
        {rows.map((row) => (
          <div key={row.id} className={`min-w-0 @[1000px]:border-r @[1000px]:border-[var(--hairline)] @[1000px]:px-4 @[1000px]:first:pl-0`} data-closure-metal={row.metal}>
            <div className="mb-2 flex items-baseline gap-2">
              <span className="text-xs font-semibold uppercase tracking-[0.07em] text-muted">{row.metal}</span>
              <span className="text-lg font-semibold tabular-nums text-ink-primary">{row.errorLabel}</span>
              <span className="flex-1" />
              <StateChipPill tone={row.statusTone === 'ok' ? 'ok' : 'warn'}>{row.status}</StateChipPill>
            </div>
            <DivergingBar
              geometry={closureBarGeometry(row.errorPct)}
              tone={row.statusTone === 'ok' ? 'ok' : 'warn'}
              scalePct={MASS_BALANCE_SCALE_PCT}
              tolerancePct={MASS_BALANCE_TOLERANCE_PCT}
            />
          </div>
        ))}
        <div className={`flex flex-wrap items-center gap-2 text-xs text-muted @[1000px]:flex-col @[1000px]:items-start @[1000px]:justify-center @[1000px]:pl-4`}>
          <span className="inline-flex items-center gap-1.5">
            Data quality
            <CaseCodeChip code={caseCodeFor('case-wit-021')} label="sole fresh-feed anchor" onOpen={() => onOpenCase('case-wit-021', 'WIT-021 sole fresh-feed anchor')} />
          </span>
          <span>sole fresh-feed anchor</span>
          <Link
            to={projectSidebarHref(projectSlug, 'metal-accounting')}
            className="inline-flex h-6 items-center whitespace-nowrap rounded-lg border border-[var(--hairline-strong)] px-2 text-xs text-ink-secondary transition-colors duration-150 hover:bg-[var(--surface-1)] hover:text-ink-primary"
          >
            Design-basis reconciliation
          </Link>
        </div>
      </div>
    </DowntimeCard>
  );
}

function SamplePointsDisclosure({ cadence }: { cadence: PlantCadence }) {
  const [open, setOpen] = useState(false);
  const inventory = sampleSourceInventory(cadence);
  const processCount = inventory.filter((source) => source.samplePointId).length;
  return (
    <details open={open} onToggle={(event) => setOpen(event.currentTarget.open)} className="card-matte-2 rounded-2xl" data-sample-points>
      <summary className="flex cursor-pointer list-none items-center gap-2.5 rounded-2xl px-4 py-3 transition-colors duration-150 hover:bg-[var(--surface-1)] [&::-webkit-details-marker]:hidden">
        <ChevronRight aria-hidden="true" className={`h-3.5 w-3.5 shrink-0 text-muted transition-transform duration-200 ${open ? 'rotate-90' : ''}`} />
        <span className="flex flex-wrap items-baseline gap-x-2.5">
          <span className="text-sm font-semibold text-ink-primary">Sample points</span>
          <span className="text-xs text-muted">
            {sampleSourceCount()} streams · SA01–SA08 process + {sampleSourceCount() - processCount} non-process
          </span>
        </span>
      </summary>
      {open ? (
        <div className="px-4 pb-4">
          <div className={CARD_TABLE_DENSE}>
            <Table wrap={false} className="tabular-nums">
              <THead>
                <TR>
                  <TH className="w-20">ID</TH>
                  <TH>Stream</TH>
                  <TH>Frequency</TH>
                </TR>
              </THead>
              <TBody>
                {inventory.map((source) => (
                  <TR key={source.samplePointId ?? source.stream}>
                    <TD className="font-mono text-xs !text-muted">{source.samplePointId ?? '—'}</TD>
                    <TD className="font-medium !text-ink-primary">{source.stream}</TD>
                    <TD>{source.frequency}</TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </div>
        </div>
      ) : null}
    </details>
  );
}

// ---------------------------------------------------------------- impurities

function ImpuritiesTab({ onOpenCase }: { onOpenCase: OpenCase }) {
  const cells = arsenicCircuitCells();
  const trend = arsenicTrend30d();
  const artefactLine = arsenicArtefactCheckLine();
  const columns = cells.map((cell) => (cell.kind === 'arsenic' ? '1.2fr' : '0.95fr')).join(' ');
  const actionCode = caseCodeFor(IMPURITIES_ACTION.caseId);
  const openAction = () => onOpenCase(IMPURITIES_ACTION.caseId, 'Arsenic trend in Pb/Ag concentrate');

  return (
    <div className="space-y-3">
      <GlassCard
        as="section"
        tone="matte"
        aria-label="Arsenic circuit distribution"
        data-arsenic-circuit
        className="overflow-hidden rounded-2xl border-[var(--hairline-strong)] p-0"
      >
        <div className="grid grid-cols-3 @[880px]:[grid-template-columns:var(--as-cols)] [&>*]:min-h-[7.25rem]" style={{ ['--as-cols' as string]: columns }}>
          {cells.map((cell) =>
            cell.kind === 'condition' ? (
              <StripCell key={cell.id} id={cell.id} name={cell.name} figure={cell.value} quiet className="border-b border-[var(--hairline)] @[880px]:border-b-0 after:hidden @[880px]:after:block">
                <span className="break-words text-xs leading-snug text-muted">{cell.line}</span>
              </StripCell>
            ) : (
              <StripCell
                key={cell.id}
                id={cell.id}
                name={cell.name}
                figure={cell.value}
                figureContext="As"
                className="border-b border-[var(--hairline)] @[880px]:border-b-0 after:hidden @[880px]:after:block"
                foot={
                  <span className="flex w-full min-w-0 flex-col gap-1.5">
                    {cell.sharePct !== undefined ? (
                      <span className="flex min-w-0 items-center gap-2 text-xs text-muted" data-as-share={cell.id}>
                        <RankBar className="flex-1" ratio={cell.sharePct / 100} tone={cell.status?.tone === 'warn' ? 'warn' : 'ink'} />
                        <span className="whitespace-nowrap tabular-nums">{cell.shareLabel}</span>
                      </span>
                    ) : null}
                    {cell.status ? (
                      <span>
                        <StateChipPill tone={cell.status.tone}>{cell.status.label}</StateChipPill>
                      </span>
                    ) : null}
                  </span>
                }
              >
                <span className="break-words text-xs leading-snug text-muted">{cell.line}</span>
                {cell.trend ? <Comparison lead={cell.trend.lead} rest={cell.trend.rest} /> : null}
              </StripCell>
            ),
          )}
        </div>
      </GlassCard>

      <div className={`grid min-w-0 gap-3 @[1000px]:grid-cols-12 @[1000px]:items-start`}>
        <DowntimeCard
          title="30-day arsenic trend in concentrates"
          meta="dashed = smelter limit"
          actions={SYN_CHIP}
          className={`@[1000px]:col-span-7`}
          aria-label="30-day arsenic trend"
        >
          <TrendChart
            kind="line"
            categories={trend.categories}
            series={trend.series}
            references={trend.references}
            height={230}
            yDomain={[0, 0.6]}
            labelLastPoint
            labelLastPointSeries="Pb/Ag conc"
            referenceLabelTone
            legendNote="daily average"
            pointMarkers={false}
          />
          {artefactLine ? <p className="mt-2 text-sm font-medium text-[var(--state-warning)]" data-artefact-line>{artefactLine}</p> : null}
        </DowntimeCard>
        <ConcentratePenaltyPanel className={`@[1000px]:col-span-5`} />
      </div>

      <GlassCard as="section" tone="matte" aria-label="Action" data-impurities-action className="px-4 py-3">
        <div className="flex flex-wrap items-center gap-x-3.5 gap-y-2">
          <span className="text-xs font-semibold uppercase tracking-[0.07em] text-muted">Action</span>
          <CaseCodeChip code={actionCode} label="Arsenic trend in Pb/Ag concentrate" onOpen={openAction} />
          <span className="text-sm font-semibold text-ink-primary">{IMPURITIES_ACTION.headline}</span>
          <span className="flex-1" />
          <Button variant="ghost" className="h-8 border border-[var(--hairline-strong)] px-3 text-xs" onClick={openAction}>
            Open case
          </Button>
        </div>
        <p className="mt-1 text-sm text-ink-secondary">{IMPURITIES_ACTION.detail}</p>
      </GlassCard>
    </div>
  );
}

// ---------------------------------------------------------------- samples due

/** The write paths ship later; they stay visible and disabled, and their
 *  tooltip names why (the `v1.1` pill is gone: DESIGN.md § Show, don't tell).
 *  The comp's disabled treatment (`.btn:disabled`, opacity .55 · not-allowed)
 *  is carried here, plus `aria-disabled` on each button. */
const WRITE_PATH_TITLE = 'Sample collection write path ships in v1.1';
const WRITE_PATH_BUTTON =
  'h-8 border border-[var(--hairline-strong)] px-3 text-xs disabled:cursor-not-allowed disabled:opacity-[0.55] disabled:text-muted disabled:hover:bg-transparent disabled:hover:text-muted';

function SamplesDueTab({ lists, onOpenCase }: { lists: ReturnType<typeof samplesDueLists>; onOpenCase: OpenCase }) {
  const dueNow = samplesDueRowsView(lists.dueNow);
  const pending = samplesDueRowsView(lists.collectedPending);
  const missed = samplesDueRowsView(lists.missed);
  // The card's stream count and the strip's Due figure are the same read of the
  // same list — `dueNowCount()` — so the two can never disagree (issue #658
  // round 2: this was a local `filter(!continuation).length`).
  const dueStreams = dueNowCount(lists);
  const missedCaseId = missedCaseIdIn(missed);

  return (
    <div className={`grid min-w-0 gap-3 @[640px]:grid-cols-3 @[1000px]:items-start`}>
      <DowntimeCard
        title="Due now"
        meta={<span data-due-streams={dueStreams}>{`${dueStreams} ${dueStreams === 1 ? 'stream' : 'streams'}`}</span>}
        actions={<ActorChip actor="Human">Operator</ActorChip>}
        aria-label="Due now"
      >
        <DueList rows={dueNow} />
      </DowntimeCard>
      <DowntimeCard
        title="Collected, LIMS pending"
        meta={`${pending.length} ${pending.length === 1 ? 'sample' : 'samples'}`}
        aria-label="Collected, LIMS pending"
      >
        <DueList rows={pending} />
        <DueFoot>
          <Button variant="ghost" disabled aria-disabled="true" title={WRITE_PATH_TITLE} className={WRITE_PATH_BUTTON}>
            Register all in LIMS
          </Button>
        </DueFoot>
      </DowntimeCard>
      <DowntimeCard
        title="Missed"
        meta={`${missed.length} ${missed.length === 1 ? 'sample' : 'samples'}${missedCaseId ? ' · discounts' : ''}`}
        actions={
          missedCaseId ? (
            <CaseCodeChip code={caseCodeFor(missedCaseId)} label="missed sample" onOpen={() => onOpenCase(missedCaseId, `${missed[0]?.stream ?? 'Sample'} ${missed[0]?.slot ?? ''} missed`)} />
          ) : undefined
        }
        aria-label="Missed"
      >
        <DueList rows={missed} onOpenCase={onOpenCase} />
        <DueFoot>
          <Button variant="ghost" disabled aria-disabled="true" title={WRITE_PATH_TITLE} className={WRITE_PATH_BUTTON}>
            Collect
          </Button>
          <Button variant="ghost" disabled aria-disabled="true" title={WRITE_PATH_TITLE} className={WRITE_PATH_BUTTON}>
            Note in LIMS
          </Button>
        </DueFoot>
      </DowntimeCard>
    </div>
  );
}

function DueFoot({ children }: { children: ReactNode }) {
  return <div className="mt-1.5 flex flex-wrap gap-2 border-t border-[var(--hairline)] pt-2.5">{children}</div>;
}

function DueList({ rows, onOpenCase }: { rows: readonly SampleDueRowView[]; onOpenCase?: OpenCase }) {
  return (
    <ul className="divide-y divide-[var(--color-divider)]" data-due-list>
      {rows.map((row) => {
        const chip = <StateChip state={row.state} />;
        return (
          <li
            key={row.id}
            data-due-row={row.id}
            className={cn('flex min-h-[44px] items-center gap-2.5 py-2 text-sm', row.continuation ? 'ml-5 border-l border-[var(--hairline)] pl-3' : '')}
          >
            <span className="min-w-0 flex-1">
              {row.continuation ? null : (
                <span className="font-semibold text-ink-primary">
                  {row.stream}
                  {row.samplePointId ? <span className="ml-1.5 font-mono text-[10px] font-normal text-muted">{row.samplePointId}</span> : null}
                </span>
              )}
              {row.detail ? <span className="block text-xs text-muted">{row.detail}</span> : null}
            </span>
            <span className="whitespace-nowrap font-semibold tabular-nums text-ink-primary">{row.slot}</span>
            {row.caseId && onOpenCase ? (
              <button
                type="button"
                aria-label={`Open case ${caseCodeFor(row.caseId)} · ${row.stream}`}
                onClick={() => onOpenCase(row.caseId!, `${row.stream} ${row.slot} missed`)}
                className="inline-flex rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-500)]"
              >
                {chip}
              </button>
            ) : (
              chip
            )}
          </li>
        );
      })}
    </ul>
  );
}

